'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Button } from './ui/button';

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center bg-background overflow-hidden">
      {/* Warm gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/50" />

      {/* Decorative elements with more presence */}
      <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-accent/[0.03] to-transparent" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float will-change-transform" />
      <div className="absolute bottom-1/3 left-1/6 w-72 h-72 bg-primary/[0.05] rounded-full blur-3xl" />

      {/* Geometric accents */}
      <div className="hidden lg:block absolute top-32 left-12">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-accent/40 to-transparent" />
      </div>
      <div className="hidden lg:block absolute top-40 left-16">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
      </div>
      <div className="hidden lg:block absolute bottom-32 right-12">
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
                          linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Text content - takes 7 columns on large screens */}
          <div className="lg:col-span-7 space-y-6 md:space-y-8 order-2 lg:order-1">
            {/* Animated tagline */}
            <div className="opacity-0 animate-fade-up">
              <span className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase text-accent">
                <span className="w-8 h-px bg-accent" />
                Speaker · Consultant · Leader
              </span>
            </div>

            {/* Name with staggered animation */}
            <h1 className="space-y-1">
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tight leading-[0.9] opacity-0 animate-fade-up animation-delay-100">
                Andrew
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold tracking-tight leading-[0.9] opacity-0 animate-fade-up animation-delay-200">
                McCourt
              </span>
            </h1>

            {/* Tagline with accent */}
            <p className="text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-xl leading-relaxed opacity-0 animate-fade-up animation-delay-300">
              Guiding individuals, churches and organizations toward{' '}
              <span className="text-foreground font-medium">meaningful growth</span>{' '}
              through strategic insight and faith-based leadership.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 opacity-0 animate-fade-up animation-delay-400">
              <Button
                asChild
                size="lg"
                className="group text-base font-medium h-14 px-8 bg-primary hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
              >
                <Link href="/speaking" className="flex items-center gap-2">
                  Book for Speaking
                  <svg
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="group text-base font-medium h-14 px-8 border-2 border-primary/20 hover:border-primary/40 hover:bg-primary/5 hover:text-foreground transition-all duration-300"
              >
                <Link href="/consulting" className="flex items-center gap-2">
                  Consulting Services
                  <svg
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </Button>
            </div>
          </div>

          {/* Image - takes 5 columns, with offset styling */}
          {/* Note: Removed opacity-0 from container to ensure LCP element is detected */}
          <div className="lg:col-span-5 order-1 lg:order-2 animate-scale-in animation-delay-200">
            <div className="relative max-w-[320px] sm:max-w-sm md:max-w-md lg:max-w-none mx-auto lg:ml-auto">
              {/* Decorative frame offset */}
              <div className="absolute -inset-4 border border-accent/20 -z-10 translate-x-4 translate-y-4" />

              {/* Main image container */}
              <div className="relative aspect-[4/5] bg-gradient-to-br from-muted to-muted/50 overflow-hidden shadow-2xl shadow-primary/10">
                <Image
                  src="/andrew-mccourt.jpg"
                  alt="Andrew McCourt"
                  fill
                  className="object-cover object-center"
                  priority
                  sizes="(max-width: 640px) 320px, (max-width: 768px) 384px, (max-width: 1024px) 448px, 500px"
                />

                {/* Subtle gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent" />
              </div>

              {/* Accent corner */}
              <div className="absolute -bottom-2 -left-2 w-12 h-12 border-l-2 border-b-2 border-accent/40" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 opacity-0 animate-fade-in animation-delay-800">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-accent/50 to-transparent" />
      </div>
    </section>
  );
}
